import Breadcrumb from "components/Common/Breadcrumb"
import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { Col, Row, Card, CardBody, Container, Label, Input, Button, Spinner } from "reactstrap"
import { getAddonCatalog, updateAddonCatalog, downloadWhatsappTrialReport, downloadJobManagementTrialReport } from "helpers/backendHelpers/manageSubscription"
import AdminWhatsappCard from "pages/admin-whatsapp"
import RenewalReminderTemplateCard from "components/RenewalReminderTemplateCard"
import { StatusSwitch, SalesUsersSection, SessionLogoutCard } from "components/AdminAccessCards"
import CustomerSupportCard from "components/CustomerSupportCard"
import WebVersionUpdateCard from "components/WebVersionUpdateCard"
import SubscriptionOfferCard from "components/SubscriptionOfferCard"
import ReportExportCards from "components/ReportExportCards"
import AccountingPartnerReport from "components/AccountingPartnerReport"

const SECTIONS = [
  { key: "general", label: "General", icon: "bx bx-cog", description: "Web app version and customer support numbers" },
  { key: "offers", label: "Offers", icon: "bx bx-gift", description: "Promotional offers for subscriptions" },
  { key: "addons", label: "Add-on Catalog", icon: "bx bx-package", description: "Pricing and availability of paid add-ons" },
  { key: "messaging", label: "Messaging", icon: "bx bxl-whatsapp", description: "WhatsApp connection and renewal reminders" },
  { key: "exports", label: "Exports", icon: "bx bx-download", description: "Download Excel reports for any date range" },
  { key: "accountingPartners", label: "Accounting Partners", icon: "bx bx-line-chart", description: "Accounting partner report: leads, conversion and total selling" },
  { key: "salesUsers", label: "Sales Users", icon: "bx bx-user-circle", description: "Create sales accounts and control who can sign in" },
  { key: "security", label: "Security", icon: "bx bx-shield-quarter", description: "Active sessions" },
]

const SettingCardHeader = ({ title, subtitle, action }) => (
  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
    <div>
      <h5 className="font-size-16 fw-bold mb-1">{title}</h5>
      {subtitle && <p className="text-muted mb-0 font-size-13">{subtitle}</p>}
    </div>
    {action}
  </div>
)

const TrialReportButton = ({ loading, onClick }) => (
  <Button color="success" outline size="sm" type="button" disabled={loading} onClick={onClick}>
    {loading ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-download me-1"></i>}
    15 day trial excel
  </Button>
)

// One card layout shared by every add-on catalog entry.
const AddonCatalogCard = ({ title, subtitle, form, setForm, fields, onSave, saving, loading, notFound, headerAction }) => (
  <Card className="mb-3">
    <CardBody>
      <SettingCardHeader title={title} subtitle={subtitle} action={headerAction} />
      {loading ? (
        <div className="text-center py-4">
          <Spinner color="primary" />
        </div>
      ) : form ? (
        <>
          <Row>
            {fields.map(field => (
              <Col key={field.key} md={field.md || 4} className="mb-3">
                <Label className="form-label">{field.label}</Label>
                <Input
                  type={field.type || "text"}
                  min={field.min}
                  value={form[field.key]}
                  onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                />
              </Col>
            ))}
          </Row>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 border-top pt-3">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted font-size-13">Availability</span>
              <StatusSwitch checked={form.isActive} onChange={next => setForm(prev => ({ ...prev, isActive: next }))} />
            </div>
            <Button color="primary" type="button" disabled={saving} onClick={onSave}>
              {saving ? <Spinner size="sm" /> : "Save changes"}
            </Button>
          </div>
        </>
      ) : (
        <div className="text-muted">{notFound}</div>
      )}
    </CardBody>
  </Card>
)

const WHATSAPP_FIELDS = [
  { key: "price", label: "Annual Price (Before GST)", type: "number", min: "0", md: 3 },
  { key: "defaultDurationDays", label: "Duration (Days)", type: "number", min: "1", md: 3 },
  { key: "description", label: "Description", md: 6 },
]

const EWAY_FIELDS = [
  { key: "creditsPerUnit", label: "Credits Per Unit", type: "number", min: "0", md: 3 },
  { key: "pricePerUnit", label: "Price Per Unit", type: "number", min: "0", md: 3 },
  { key: "pricePerCredit", label: "Price Per Credit", type: "number", min: "0", md: 3 },
  { key: "defaultDurationDays", label: "Duration (Days)", type: "number", min: "1", md: 3 },
  { key: "description", label: "Description", md: 12 },
]

const Index = () => {
  // The section lives in the URL (?section=...), so a refresh, the back button and the partner
  // detail page's back link all return to the same section.
  const history = useHistory()
  const location = useLocation()
  const requestedSection = new URLSearchParams(location.search).get("section")
  const activeSection = SECTIONS.some(section => section.key === requestedSection) ? requestedSection : "general"
  const setActiveSection = key => history.push({ pathname: location.pathname, search: `?section=${key}` })
  useEffect(() => {
    if (requestedSection !== activeSection) history.replace({ pathname: location.pathname, search: `?section=${activeSection}` })
  }, [requestedSection, activeSection, history, location.pathname])
  const [whatsappCatalogForm, setWhatsappCatalogForm] = useState(null)
  const [jobManagementCatalogForm, setJobManagementCatalogForm] = useState(null)
  const [ewayCatalogForm, setEwayCatalogForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [ewaySubmitLoading, setEwaySubmitLoading] = useState(false)
  const [jobManagementSubmitLoading, setJobManagementSubmitLoading] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [jobManagementDownloadLoading, setJobManagementDownloadLoading] = useState(false)

  const handleDownloadReport = async () => {
    try {
      setDownloadLoading(true)
      await downloadWhatsappTrialReport()
    } catch (error) {
      console.error("Failed to download trial report:", error)
    } finally {
      setDownloadLoading(false)
    }
  }

  const handleJobManagementDownloadReport = async () => {
    try {
      setJobManagementDownloadLoading(true)
      await downloadJobManagementTrialReport()
    } catch (error) {
      console.error("Failed to download Job Management trial report:", error)
    } finally {
      setJobManagementDownloadLoading(false)
    }
  }

  useEffect(() => {
    fetchAddonCatalogData()
  }, [])

  const fetchAddonCatalogData = async () => {
    try {
      setLoading(true)
      let response = await getAddonCatalog()
      response = response?.payload || []
      const whatsappAddon = response.find(item => item.code === "whatsappService")
      setWhatsappCatalogForm(whatsappAddon ? {
        _id: whatsappAddon._id,
        price: whatsappAddon.price || 0,
        defaultDurationDays: whatsappAddon.defaultDurationDays || 365,
        description: whatsappAddon.description || "",
        isActive: whatsappAddon.isActive !== false
      } : null)

      const jobManagementAddon = response.find(item => item.code === "jobManagement")
      setJobManagementCatalogForm(jobManagementAddon ? {
        _id: jobManagementAddon._id,
        price: jobManagementAddon.price || 0,
        defaultDurationDays: jobManagementAddon.defaultDurationDays || 365,
        description: jobManagementAddon.description || "",
        isActive: jobManagementAddon.isActive !== false
      } : null)

      const ewayAddon = response.find(item => item.code === "ewayBill")
      setEwayCatalogForm(ewayAddon ? {
        _id: ewayAddon._id,
        defaultDurationDays: ewayAddon.defaultDurationDays || 365,
        description: ewayAddon.description || "",
        isActive: ewayAddon.isActive !== false,
        creditsPerUnit: ewayAddon.creditConfig?.creditsPerUnit || 0,
        pricePerUnit: ewayAddon.creditConfig?.pricePerUnit || 0,
        pricePerCredit: ewayAddon.creditConfig?.pricePerCredit || 0
      } : null)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const saveWhatsappCatalog = async () => {
    if (!whatsappCatalogForm?._id) return
    setSubmitLoading(true)
    try {
      await updateAddonCatalog(whatsappCatalogForm._id, {
        price: Number(whatsappCatalogForm.price),
        defaultDurationDays: Number(whatsappCatalogForm.defaultDurationDays),
        description: whatsappCatalogForm.description,
        isActive: whatsappCatalogForm.isActive
      })
      await fetchAddonCatalogData()
    } catch (error) {
      console.error("WhatsApp catalog update failed:", error?.response?.data?.message || error?.message)
    } finally {
      setSubmitLoading(false)
    }
  }

  const saveJobManagementCatalog = async () => {
    if (!jobManagementCatalogForm?._id) return
    setJobManagementSubmitLoading(true)
    try {
      await updateAddonCatalog(jobManagementCatalogForm._id, {
        price: Number(jobManagementCatalogForm.price),
        defaultDurationDays: Number(jobManagementCatalogForm.defaultDurationDays),
        description: jobManagementCatalogForm.description,
        isActive: jobManagementCatalogForm.isActive
      })
      await fetchAddonCatalogData()
    } catch (error) {
      console.error("Job Management catalog update failed:", error?.response?.data?.message || error?.message)
    } finally {
      setJobManagementSubmitLoading(false)
    }
  }

  const saveEwayCatalog = async () => {
    if (!ewayCatalogForm?._id) return
    setEwaySubmitLoading(true)
    try {
      await updateAddonCatalog(ewayCatalogForm._id, {
        defaultDurationDays: Number(ewayCatalogForm.defaultDurationDays),
        description: ewayCatalogForm.description,
        isActive: ewayCatalogForm.isActive,
        creditConfig: {
          creditsPerUnit: Number(ewayCatalogForm.creditsPerUnit),
          pricePerUnit: Number(ewayCatalogForm.pricePerUnit),
          pricePerCredit: Number(ewayCatalogForm.pricePerCredit)
        }
      })
      await fetchAddonCatalogData()
    } catch (error) {
      console.error("E-Way Bill catalog update failed:", error?.response?.data?.message || error?.message)
    } finally {
      setEwaySubmitLoading(false)
    }
  }

  const current = SECTIONS.find(section => section.key === activeSection)

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Setting" breadcrumbItem="Global Setting" />
        <Row>
          <Col lg={3} xl={2} className="mb-3">
            <style>{`@media (min-width: 992px) { .main-content { overflow: clip !important; } .setting-nav-sticky { position: sticky; top: 90px; } }`}</style>
            <Card className="mb-0 setting-nav-sticky">
              <CardBody className="p-2">
                <div className="d-flex flex-row flex-lg-column gap-1 overflow-auto">
                  {SECTIONS.map(section => {
                    const isActive = section.key === activeSection
                    return (
                      <button
                        key={section.key}
                        type="button"
                        onClick={() => setActiveSection(section.key)}
                        className={`btn text-start d-flex align-items-center gap-2 border-0 text-nowrap ${isActive ? "btn-primary" : "btn-light text-body"}`}
                        style={{ fontWeight: isActive ? 600 : 500 }}
                      >
                        <i className={`${section.icon} font-size-18`}></i>
                        {section.label}
                      </button>
                    )
                  })}
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={9} xl={10}>
            <div className="mb-3">
              <h4 className="font-size-18 mb-1">{current.label}</h4>
              <p className="text-muted mb-0">{current.description}</p>
            </div>

            {activeSection === "general" && (
              <>
                <WebVersionUpdateCard />
                <CustomerSupportCard />
              </>
            )}

            {activeSection === "offers" && (
              <>
                <SubscriptionOfferCard />
                <SubscriptionOfferCard
                  settingKey="addonOfferEnabled"
                  title="Add-on Free Months Offer"
                  description="1 year add-on: +1 month free, 3 year add-on: +3 months free (Job Management, WhatsApp Service). When off, no free months are given on add-ons."
                />
              </>
            )}

            {activeSection === "addons" && (
              <>
                <AddonCatalogCard
                  title="WhatsApp Service"
                  subtitle="Annual add-on for WhatsApp document automation"
                  form={whatsappCatalogForm}
                  setForm={setWhatsappCatalogForm}
                  fields={WHATSAPP_FIELDS}
                  onSave={saveWhatsappCatalog}
                  saving={submitLoading}
                  loading={loading}
                  notFound="WhatsApp service catalog not found."
                  headerAction={<TrialReportButton loading={downloadLoading} onClick={handleDownloadReport} />}
                />
                <AddonCatalogCard
                  title="Job Management"
                  subtitle="Job inward, LOT, stage management and production allocation"
                  form={jobManagementCatalogForm}
                  setForm={setJobManagementCatalogForm}
                  fields={WHATSAPP_FIELDS}
                  onSave={saveJobManagementCatalog}
                  saving={jobManagementSubmitLoading}
                  loading={loading}
                  notFound={<>Job Management catalog not found. Run <code>npm run seed:addon:job-management</code>.</>}
                  headerAction={<TrialReportButton loading={jobManagementDownloadLoading} onClick={handleJobManagementDownloadReport} />}
                />
                <AddonCatalogCard
                  title="E-Way Bill"
                  subtitle="Credit based add-on for API e-way bill generation"
                  form={ewayCatalogForm}
                  setForm={setEwayCatalogForm}
                  fields={EWAY_FIELDS}
                  onSave={saveEwayCatalog}
                  saving={ewaySubmitLoading}
                  loading={loading}
                  notFound="E-Way Bill catalog not found."
                />
              </>
            )}

            {activeSection === "messaging" && (
              <>
                <AdminWhatsappCard />
                <RenewalReminderTemplateCard />
              </>
            )}

            {activeSection === "exports" && <ReportExportCards />}

            {activeSection === "accountingPartners" && <AccountingPartnerReport />}

            {activeSection === "salesUsers" && <SalesUsersSection />}

            {activeSection === "security" && <SessionLogoutCard />}
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Index
